import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { motion } from 'framer-motion';
import BackButton from './BackButton';

const semestersByBranch = {
    CSE: ['1st Semester', '2nd Semester', '3rd Semester', '4th Semester', '5th Semester', '6th Semester', '7th Semester'],
    AIML: ['1st Semester', '2nd Semester', '3rd Semester', '4th Semester', '5th Semester'],
    AIDS: ['1st Semester', '2nd Semester', '3rd Semester', '4th Semester', '5th Semester'],
    IT: ['1st Semester', '2nd Semester', '3rd Semester', '4th Semester', '5th Semester'],
};

const SemesterLayout = ({ branch, semester, children }) => {
    const navigate = useNavigate();
    const availableSemesters = semestersByBranch[branch] || semestersByBranch['CSE'];

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-main)' }}>
            <div className="max-w-[1400px] mx-auto px-4 pt-24 md:pt-32 pb-20">
                {/* Back Button */}
                <div style={{ marginBottom: '16px' }}>
                    <BackButton to="/home" label="Back to Home" />
                </div>

                {/* Modern Breadcrumbs */}
                <nav className="flex items-center gap-2 text-[13px] mb-8 font-medium tracking-tight overflow-x-auto no-scrollbar whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                    <Link to="/home" className="hover:text-[var(--text-main)] transition-colors" style={{ color: 'inherit' }}>Universities</Link>
                    <FiChevronRight className="opacity-50 flex-shrink-0" />
                    <span className="flex-shrink-0">GGSIPU</span>
                    <FiChevronRight className="opacity-50 flex-shrink-0" />
                    <Link to="/home" className="hover:text-[var(--text-main)] transition-colors flex-shrink-0" style={{ color: 'inherit' }}>B.Tech</Link>
                    <FiChevronRight className="opacity-50 flex-shrink-0" />
                    <span style={{ color: 'var(--text-main)' }} className="flex-shrink-0">{semester}</span>
                    <FiChevronRight className="opacity-50 flex-shrink-0" />
                    <span style={{ color: 'var(--text-main)' }} className="flex-shrink-0">{branch}</span>
                </nav>

                {/* Selectors Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '2rem', padding: '2rem', boxShadow: 'var(--card-shadow)' }}>
                        <label className="block text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>Semester</label>
                        <div className="relative group">
                            <select
                                value={semester}
                                onChange={(e) => {
                                    const sem = e.target.value.split(' ')[0].toLowerCase();
                                    navigate(`/btech/${branch.toLowerCase()}/${sem}`);
                                }}
                                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '1rem', padding: '1rem 1.25rem' }}
                                className="w-full appearance-none text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/5 transition-all cursor-pointer"
                            >
                                {availableSemesters.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <FiChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 transition-colors pointer-events-none" style={{ color: 'var(--text-muted)' }} />
                        </div>
                    </div>

                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '2rem', padding: '2rem', boxShadow: 'var(--card-shadow)' }}>
                        <label className="block text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>Branch</label>
                        <div className="relative group">
                            <select
                                value={branch}
                                onChange={(e) => {
                                    const newBranch = e.target.value;
                                    const targetSemesters = semestersByBranch[newBranch] || semestersByBranch['CSE'];
                                    const finalSem = targetSemesters.includes(semester) ? semester : targetSemesters[targetSemesters.length - 1];
                                    const semNum = finalSem.split(' ')[0].toLowerCase();
                                    navigate(`/btech/${newBranch.toLowerCase()}/${semNum}`);
                                }}
                                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '1rem', padding: '1rem 1.25rem' }}
                                className="w-full appearance-none text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/5 transition-all cursor-pointer"
                            >
                                {['CSE', 'AIML', 'AIDS', 'IT'].map(b => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>
                            <FiChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 transition-colors pointer-events-none" style={{ color: 'var(--text-muted)' }} />
                        </div>
                    </div>
                </div>

                {/* Premium Header */}
                <header className="mb-16 relative">
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-500/10 rounded-full blur-[100px] pointer-events-none" />

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-widest mb-6">
                            Engineering Notes
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4" style={{ color: 'var(--text-main)' }}>
                            {branch} <span style={{ color: 'var(--text-muted)' }}>{semester}</span>
                        </h1>
                        <p style={{ color: 'var(--text-muted)' }} className="text-lg max-w-2xl leading-relaxed">
                            Premium study materials, previous year questions, and high-quality notes meticulously curated for your academic excellence.
                        </p>
                    </motion.div>
                </header>

                {/* Content Section */}
                <main>
                    {children}
                </main>
            </div>
        </div>
    );
};

export default SemesterLayout;
